import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import MissionalScale from './MissionalScale';
import LoadingSpinner from './LoadingSpinner';
import { toast } from 'react-hot-toast';
import useMissionalScaleLevel from '../hooks/useMissionalScaleLevel';

const discipleshipStages = [
  "Select Stage...",
  "Not currently a believer",
  "New Believer",
  "Growing Disciple",
  "Disciple Maker",
  "Sage stage"
];
const ageRanges = [
  "Select Age Range...",
  "Elementary Age",
  "Middle School",
  "High School",
  "Young Adult",
  "Middle Age",
  "Wisdom Years"
];
const christianExperiences = [
  "Select Experience...",
  "No Christian Faith experience yet",
  "New to the Christian Faith",
  "Walking with God - Grew up in the Faith",
  "Walking with God - Came to Faith later in life",
  "Currently in a crisis of Faith - leaning in",
  "Currently in a crisis of Faith - leaning out",
  "Walked Away from the Faith Currently",
  "Walked Away from the Faith at one point, but is now Open Again",
  "Walked Away, but has now returned to the Faith"
];
const relationshipStatuses = [
  "Select Status...",
  "Youth",
  "Single Young Adult",
  "Single Adult",
  "Dating",
  "Engaged",
  "Early Marriage",
  "Experienced Marriage",
  "Married with young kids",
  "Married with older kids",
  "Grandparents",
  "Separated",
  "Divorced",
  "Widowed"
];

const FaithJourneyDrawerContent = () => {
  const { discipleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [discipleData, setDiscipleData] = useState(null);
  const [mentorDetails, setMentorDetails] = useState(null);
  const [userAppID, setUserAppID] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [faithJourneyStage, setFaithJourneyStage] = useState(discipleshipStages[0]);
  const [faithJourneyAgeRange, setFaithJourneyAgeRange] = useState(ageRanges[0]);
  const [faithJourneyExperience, setFaithJourneyExperience] = useState(christianExperiences[0]);
  const [faithJourneyRelationship, setFaithJourneyRelationship] = useState(relationshipStatuses[0]);
  const [faithJourneyContext, setFaithJourneyContext] = useState('');

  const [stageComment, setStageComment] = useState('');
  const [ageComment, setAgeComment] = useState('');
  const [experienceComment, setExperienceComment] = useState('');
  const [relationshipComment, setRelationshipComment] = useState('');

  const [showStageComment, setShowStageComment] = useState(false);
  const [showAgeComment, setShowAgeComment] = useState(false);
  const [showExperienceComment, setShowExperienceComment] = useState(false);
  const [showRelationshipComment, setShowRelationshipComment] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !discipleId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const appID = userDoc.exists() ? userDoc.data().appID : null;
        if (!appID) throw new Error("User appID not found.");
        setUserAppID(appID);
        const [discipleDoc, mentorDetailsDoc] = await Promise.all([
          getDoc(doc(db, 'disciples', discipleId)),
          getDoc(doc(db, 'disciples', discipleId, 'mentorDetails', appID))
        ]);
        if (discipleDoc.exists()) setDiscipleData(discipleDoc.data());
        if (mentorDetailsDoc.exists()) {
          const details = mentorDetailsDoc.data();
          setMentorDetails(details);
          setFaithJourneyStage(details.faithJourneyStage || discipleshipStages[0]);
          setFaithJourneyAgeRange(details.faithJourneyAgeRange || ageRanges[0]);
          setFaithJourneyExperience(details.faithJourneyExperience || christianExperiences[0]);
          setFaithJourneyRelationship(details.faithJourneyRelationship || relationshipStatuses[0]);
          setFaithJourneyContext(details.faithJourneyContext || '');
          setStageComment(details.faithJourneyStageComment || '');
          setAgeComment(details.faithJourneyAgeRangeComment || '');
          setExperienceComment(details.faithJourneyExperienceComment || '');
          setRelationshipComment(details.faithJourneyRelationshipComment || '');
        }
      } catch (error) {
        console.error('Error fetching data for FaithJourneyDrawerContent:', error);
        toast.error("Failed to load disciple data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser, discipleId]);

  const updateDiscipleMissionalScale = async (value) => {
    if (!discipleId || !userAppID) return;
    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      await setDoc(mentorDetailsRef, {
        missionalScale: value,
        lastUpdated: serverTimestamp()
      }, { merge: true });
      setMentorDetails(prev => ({ ...(prev || {}), missionalScale: value }));
    } catch (error) {
      console.error('Error updating missional scale:', error);
      toast.error('Failed to save scale position.');
    }
  };

  const handleSaveChanges = async () => {
    if (!discipleId || !userAppID) return;
    setIsSaving(true);
    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      const journeyData = {
        faithJourneyStage: faithJourneyStage === discipleshipStages[0] ? null : faithJourneyStage,
        faithJourneyAgeRange: faithJourneyAgeRange === ageRanges[0] ? null : faithJourneyAgeRange,
        faithJourneyExperience: faithJourneyExperience === christianExperiences[0] ? null : faithJourneyExperience,
        faithJourneyRelationship: faithJourneyRelationship === relationshipStatuses[0] ? null : faithJourneyRelationship,
        faithJourneyContext: faithJourneyContext,
        faithJourneyStageComment: stageComment,
        faithJourneyAgeRangeComment: ageComment,
        faithJourneyExperienceComment: experienceComment,
        faithJourneyRelationshipComment: relationshipComment,
        lastUpdated: serverTimestamp()
      };
      await setDoc(mentorDetailsRef, journeyData, { merge: true });
      toast.success("Faith Journey details saved!");
    } catch (error) {
      console.error("Error saving Faith Journey details:", error);
      toast.error("Failed to save details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const { currentLevel, descriptor, phrase, setLevel, loading: scaleLoading } = useMissionalScaleLevel(discipleId, userAppID);

  if (loading) return <LoadingSpinner />;
  if (!userAppID || !discipleData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-500">Could not load disciple data. Please ensure you are logged in and the disciple exists.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="container mx-auto px-4 py-6">
         {/* Section for the 5 Layers */}
         <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-sm text-gray-400 mb-6">Use this area to track your disciple's faith journey. 
              You can add comments to each layer to have better support for giving them the right resources.</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
                {/* Layer 1: Discipleship Stage */} 
                <div className="space-y-1"> {/* Reduced space-y */} 
                    <label htmlFor="faithJourneyStage" className="block text-sm font-medium text-gray-700">Discipleship Stage</label>
                    <select 
                        id="faithJourneyStage"
                        value={faithJourneyStage}
                        onChange={(e) => setFaithJourneyStage(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                        {discipleshipStages.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    {/* Toggle Link Container - Right Aligned */}
                    <div className="flex justify-end">
                      <button 
                        type="button"
                        onClick={() => setShowStageComment(prev => !prev)}
                        className="text-xs text-gray-400 hover:text-gray-600  focus:outline-none"
                      >
                        Comments
                      </button>
                    </div>
                    {/* Conditional Textarea */}
                    {showStageComment && (
                      <textarea
                          value={stageComment}
                          onChange={(e) => setStageComment(e.target.value)}
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm mt-1"
                          placeholder="Add specific context for stage..."
                      />
                    )}
                </div>
                {/* Layer 2: Age Range */} 
                <div className="space-y-1"> {/* Reduced space-y */} 
                    <label htmlFor="faithJourneyAgeRange" className="block text-sm font-medium text-gray-700">Age Range</label>
                    <select 
                        id="faithJourneyAgeRange"
                        value={faithJourneyAgeRange}
                        onChange={(e) => setFaithJourneyAgeRange(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                        {ageRanges.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    {/* Toggle Link Container - Right Aligned */}
                    <div className="flex justify-end">
                      <button 
                        type="button"
                        onClick={() => setShowAgeComment(prev => !prev)}
                        className="text-xs text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        Comments
                      </button>
                    </div>
                    {/* Conditional Textarea */}
                    {showAgeComment && (
                      <textarea
                          value={ageComment}
                          onChange={(e) => setAgeComment(e.target.value)}
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm mt-1"
                          placeholder="Comments..."
                      />
                    )}
                </div>
                 {/* Layer 3: Christian Experience */} 
                 <div className="space-y-1"> {/* Reduced space-y */} 
                     <label htmlFor="faithJourneyExperience" className="block text-sm font-medium text-gray-700">Christian Experience</label>
                     <select 
                         id="faithJourneyExperience"
                         value={faithJourneyExperience}
                         onChange={(e) => setFaithJourneyExperience(e.target.value)}
                         className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                     >
                         {christianExperiences.map(option => (
                             <option key={option} value={option}>{option}</option>
                         ))}
                     </select>
                     {/* Toggle Link Container - Right Aligned */}
                     <div className="flex justify-end">
                       <button 
                         type="button"
                         onClick={() => setShowExperienceComment(prev => !prev)}
                         className="text-xs text-gray-400 hover:text-gray-600 focus:outline-none"
                       >
                         Comments
                       </button>
                     </div>
                     {/* Conditional Textarea */}
                     {showExperienceComment && (
                       <textarea
                         value={experienceComment}
                         onChange={(e) => setExperienceComment(e.target.value)}
                         rows={2}
                         className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm mt-1"
                         placeholder="Add context for Christian experience (e.g., reason for crisis)..."
                       />
                     )}
                 </div>
                 {/* Layer 4: Relationship Status */} 
                 <div className="space-y-1"> {/* Reduced space-y */} 
                     <label htmlFor="faithJourneyRelationship" className="block text-sm font-medium text-gray-700">Relationship Status</label>
                     <select 
                         id="faithJourneyRelationship"
                         value={faithJourneyRelationship}
                         onChange={(e) => setFaithJourneyRelationship(e.target.value)}
                         className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                     >
                         {relationshipStatuses.map(option => (
                             <option key={option} value={option}>{option}</option>
                         ))}
                     </select>
                     {/* Toggle Link Container - Right Aligned */}
                     <div className="flex justify-end">
                       <button 
                         type="button"
                         onClick={() => setShowRelationshipComment(prev => !prev)}
                         className="text-xs text-gray-400 hover:text-gray-600 focus:outline-none"
                       >
                         Comments
                       </button>
                     </div>
                     {/* Conditional Textarea */}
                     {showRelationshipComment && (
                       <textarea
                         value={relationshipComment}
                         onChange={(e) => setRelationshipComment(e.target.value)}
                         rows={2}
                         className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm mt-1"
                         placeholder="Add context for relationship (e.g., recently married)..."
                       />
                     )}
                 </div>
            </div>
            {/* Layer 5: Missional Scale (Existing Component) */}
            <div className="mt-6 border-t pt-6">
                <div className="max-w-3xl mx-auto">
                    <MissionalScale
                        discipleId={discipleId}
                        currentUser={{ appID: userAppID }}
                        initialValue={currentLevel ?? -5}
                        onChange={setLevel}
                    />
                </div>
            </div>
            {/* Context Text Box */}
            <div className="mt-6 border-t pt-6">
                <label htmlFor="faithJourneyContext" className="block text-sm font-medium text-gray-700 mb-1">Additional Context</label>
                <textarea
                    id="faithJourneyContext"
                    rows={4}
                    value={faithJourneyContext}
                    onChange={(e) => setFaithJourneyContext(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add any other specific details or context about this disciple's journey..."
                />
            </div>
            {/* Save Button */}
            <div className="flex justify-end mt-6">
              <button
                onClick={handleSaveChanges}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
         </div>
      </div>
    </div>
  );
};

export default FaithJourneyDrawerContent; 